import { useCallback } from 'react';
import type { UseFormReturnType } from '@mantine/form';
import { useAppDispatch } from '@/app/hooks';
import { closeWorklogModal } from '@/features/worklogModalSlice';
import { updateWorklogStatus } from '@/features/worklogSlice';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { useConfirm } from '@/components/Modals/useConfirm';

interface UseUnsavedChangesParams<T> {
  form: UseFormReturnType<T>;
}

/**
 * Custom hook to handle unsaved changes when closing a modal.
 */
export const useUnsavedChanges = <T>({ form }: UseUnsavedChangesParams<T>) => {
  const confirm = useConfirm();
  const dispatch = useAppDispatch();
  const intl = useClientTerminology();

  const handleClose = useCallback(async () => {
    if (form.isDirty()) {
      const { confirmed } = await confirm({
        titleId: 'action.unsavedChanges',
        primaryLabelId: 'action.discard',
        danger: true,
        children: intl.formatMessage({
          id: 'action.unsavedChangesMessage',
        }),
      });

      if (confirmed) {
        form.reset();
        dispatch(closeWorklogModal());
        dispatch(updateWorklogStatus(false));
      }
      return;
    }
    dispatch(closeWorklogModal());
  }, [form, dispatch, confirm, intl]);

  return handleClose;
};
